import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { contractsKeys } from "@/features/contracts/hooks/use-contracts"
import {
  createOrder,
  deleteOrder,
  updateOrder,
  type OrderWriteRequest,
} from "@/features/orders/api/orders.api"
import { ordersKeys } from "@/features/orders/hooks/use-orders"
import { extractApiErrorMessage } from "@/lib/api/client"

export function useCreateOrder(contractId: string) {
  const { t } = useTranslation("orders")
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: OrderWriteRequest) => createOrder(contractId, payload),
    onSuccess: () => {
      toast.success(t("toast.createdTitle"))
      queryClient.invalidateQueries({ queryKey: ordersKeys.all })
      queryClient.invalidateQueries({
        queryKey: contractsKeys.detail(contractId),
      })
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useUpdateOrder(contractId: string, orderId: string) {
  const { t } = useTranslation("orders")
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: OrderWriteRequest) =>
      updateOrder(contractId, orderId, payload),
    onSuccess: () => {
      toast.success(t("toast.updatedTitle"))
      queryClient.invalidateQueries({ queryKey: ordersKeys.all })
      queryClient.invalidateQueries({ queryKey: ordersKeys.detail(orderId) })
      queryClient.invalidateQueries({
        queryKey: contractsKeys.detail(contractId),
      })
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useDeleteOrder(contractId: string) {
  const { t } = useTranslation("orders")
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderId: string) => deleteOrder(contractId, orderId),
    onSuccess: (_data, orderId) => {
      toast.success(t("toast.deletedTitle"))
      queryClient.invalidateQueries({ queryKey: ordersKeys.all })
      queryClient.removeQueries({ queryKey: ordersKeys.detail(orderId) })
      queryClient.invalidateQueries({
        queryKey: contractsKeys.detail(contractId),
      })
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}
